import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    Container, Typography, Box, TextField, Stack, Chip, Avatar, Skeleton, Alert,
    FormControl, InputLabel, Select, MenuItem, Button,
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Surface } from '../components/primitives';

const PAGE_SIZE = 50;

// Compact USD formatter for volume / market-cap cells.
const fmtCompactUsd = (n) => {
    if (n == null || n === 0) return '—';
    if (n >= 1e12) return `$${(n / 1e12).toFixed(1)}T`;
    if (n >= 1e9)  return `$${(n / 1e9).toFixed(1)}B`;
    if (n >= 1e6)  return `$${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3)  return `$${(n / 1e3).toFixed(0)}K`;
    return `$${n.toLocaleString()}`;
};

export default function Issuers() {
    const [issuers, setIssuers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [sector, setSector] = useState('');
    const [sort, setSort] = useState('trade_count');
    const [page, setPage] = useState(0);

    useEffect(() => {
        let cancelled = false;
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await fetch('https://api.capitolgains.io/v1/issuers');
                if (!res.ok) {
                    const errBody = await res.json().catch(() => ({}));
                    throw new Error(errBody?.error?.message || 'Failed to load issuers');
                }
                const envelope = await res.json();
                if (!cancelled) setIssuers(envelope.data || []);
            } catch (e) {
                if (!cancelled) setError(e.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchData();
        return () => { cancelled = true; };
    }, []);

    // Build sector dropdown options from the loaded data (alphabetical).
    const sectors = useMemo(() => {
        const s = new Set(issuers.map(i => i.sector).filter(Boolean));
        return [...s].sort();
    }, [issuers]);

    // Apply search + sector filter, then sort. Client-side is fine at our scale
    // (~500 unique tickers, entire list fetched in one request).
    const filtered = useMemo(() => {
        let rows = issuers;
        if (search) {
            const q = search.trim().toUpperCase();
            rows = rows.filter(i =>
                i.ticker.includes(q) ||
                (i.long_name || '').toUpperCase().includes(q)
            );
        }
        if (sector) {
            rows = rows.filter(i => i.sector === sector);
        }
        const sorters = {
            trade_count:     (a, b) => b.trade_count - a.trade_count,
            unique_members:  (a, b) => b.unique_members - a.unique_members,
            volume:          (a, b) => (b.volume_high || 0) - (a.volume_high || 0),
            last_trade_date: (a, b) => (b.last_trade_date || '').localeCompare(a.last_trade_date || ''),
            ticker:          (a, b) => a.ticker.localeCompare(b.ticker),
        };
        return [...rows].sort(sorters[sort] || sorters.trade_count);
    }, [issuers, search, sector, sort]);

    useEffect(() => { setPage(0); }, [sort]);

    const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

    return (
        <Container sx={{ pt: 6, pb: 8 }}>
            <Typography variant="h1" sx={{ color: 'text.primary', mb: 0.5 }}>
                Issuers
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                Every stock ever traded by a member of Congress.
            </Typography>

            {/* Filter bar */}
            <Surface sx={{ mb: 3 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                        size="small"
                        placeholder="Search ticker or company name"
                        value={search}
                        onChange={e => { setPage(0); setSearch(e.target.value); }}
                        fullWidth
                    />
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Sector</InputLabel>
                        <Select
                            value={sector}
                            label="Sector"
                            onChange={e => { setPage(0); setSector(e.target.value); }}
                        >
                            <MenuItem value="">All sectors</MenuItem>
                            {sectors.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Sort by</InputLabel>
                        <Select value={sort} label="Sort by" onChange={e => setSort(e.target.value)}>
                            <MenuItem value="trade_count">Most trades</MenuItem>
                            <MenuItem value="unique_members">Most politicians</MenuItem>
                            <MenuItem value="volume">Highest volume</MenuItem>
                            <MenuItem value="last_trade_date">Recently traded</MenuItem>
                            <MenuItem value="ticker">Ticker A–Z</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
            </Surface>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Typography variant="caption" sx={{ color: 'text.muted', display: 'block', mb: 2 }}>
                {filtered.length.toLocaleString()} issuer{filtered.length !== 1 ? 's' : ''}
                {(search || sector) ? ' matching filters' : ''}
            </Typography>

            <Surface padding={0}>
                {/* Table header */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: '60px 2fr 1.5fr 1fr 1fr 1fr',
                    alignItems: 'center',
                    gap: 2,
                    px: 3, py: 1.5,
                    bgcolor: 'custom.surface.subtle',
                    borderBottom: t => `1px solid ${t.palette.custom.border.subtle}`,
                }}>
                    <Typography variant="overline" sx={{ color: 'text.muted' }}>&nbsp;</Typography>
                    <Typography variant="overline" sx={{ color: 'text.muted' }}>Issuer</Typography>
                    <Typography variant="overline" sx={{ color: 'text.muted' }}>Sector</Typography>
                    <Typography variant="overline" sx={{ color: 'text.muted' }}>Trades</Typography>
                    <Typography variant="overline" sx={{ color: 'text.muted' }}>Politicians</Typography>
                    <Typography variant="overline" sx={{ color: 'text.muted' }}>Volume</Typography>
                </Box>

                {loading && [...Array(10)].map((_, i) => (
                    <Box key={i} sx={{
                        px: 3, py: 1.5,
                        borderBottom: t => `1px solid ${t.palette.custom.border.subtle}`,
                    }}>
                        <Skeleton width="70%" height={20} />
                    </Box>
                ))}

                {!loading && pageRows.length === 0 && (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: 'text.muted' }}>
                            No issuers match your filters.
                        </Typography>
                    </Box>
                )}

                {!loading && pageRows.map((iss, i) => (
                    <Box
                        key={iss.ticker}
                        component={Link}
                        to={`/issuers/${encodeURIComponent(iss.ticker)}`}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '60px 2fr 1.5fr 1fr 1fr 1fr',
                            alignItems: 'center',
                            gap: 2,
                            px: 3, py: 1.5,
                            textDecoration: 'none',
                            borderBottom: t => i < pageRows.length - 1
                                ? `1px solid ${t.palette.custom.border.subtle}`
                                : 'none',
                            transition: t => `background-color ${t.customTransitions?.fast || '150ms ease'}`,
                            '&:hover': { bgcolor: 'custom.surface.elevated' },
                        }}
                    >
                        <Avatar
                            src={`https://img.logo.dev/ticker/${iss.ticker}?token=pk_Fy4ZV5RMQEKzNJfSy9h6BA`}
                            alt={iss.ticker}
                            sx={{ width: 36, height: 36, bgcolor: '#fff' }}
                        />
                        <Box minWidth={0}>
                            <Typography sx={{
                                fontFamily: t => t.typography.fontFamilyMono,
                                color: 'text.primary',
                                fontWeight: 600,
                            }}>
                                {iss.ticker}
                            </Typography>
                            <Typography variant="caption" sx={{
                                color: 'text.muted',
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                                {iss.long_name || '—'}
                            </Typography>
                        </Box>
                        <Box>
                            {iss.sector ? (
                                <Chip
                                    label={iss.sector}
                                    size="small"
                                    sx={{
                                        bgcolor: 'custom.surface.elevated',
                                        color: 'text.secondary',
                                        border: t => `1px solid ${t.palette.custom.border.subtle}`,
                                    }}
                                />
                            ) : (
                                <Typography variant="caption" sx={{ color: 'text.muted' }}>—</Typography>
                            )}
                        </Box>
                        <Typography sx={{
                            fontFamily: t => t.typography.fontFamilyMono,
                            color: 'text.primary',
                        }}>
                            {iss.trade_count.toLocaleString()}
                        </Typography>
                        <Typography sx={{
                            fontFamily: t => t.typography.fontFamilyMono,
                            color: 'text.primary',
                        }}>
                            {iss.unique_members}
                        </Typography>
                        <Typography sx={{
                            fontFamily: t => t.typography.fontFamilyMono,
                            color: 'text.secondary',
                        }}>
                            {fmtCompactUsd(iss.volume_high)}
                        </Typography>
                    </Box>
                ))}
            </Surface>

            {/* Pagination */}
            {!loading && filtered.length > PAGE_SIZE && (
                <Stack direction="row" spacing={1.5} justifyContent="center" alignItems="center" sx={{ mt: 3 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                        startIcon={<NavigateBeforeIcon sx={{ fontSize: 16 }} />}
                    >
                        Previous
                    </Button>
                    <Typography variant="caption" sx={{ color: 'text.muted', minWidth: 100, textAlign: 'center' }}>
                        Page {page + 1} of {totalPages}
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                        disabled={page >= totalPages - 1}
                        endIcon={<NavigateNextIcon sx={{ fontSize: 16 }} />}
                    >
                        Next
                    </Button>
                </Stack>
            )}
        </Container>
    );
}
